import { For, Show, createEffect, createSignal } from "solid-js";

import CircleProgress from "~/components/CircleProgress";
import Modal from "~/components/Modal";
import { usePlayer } from "~/components/PlayerContext";
import styles from "~/components/player/SongLyricsModal.module.scss";

export default function SongLyricsModal() {
  const player = usePlayer();
  const [lyricsRef, setLyricsRef] = createSignal<HTMLDivElement>();

  const song = () => player.metadata?.song_history[0];
  const currentSong = () =>
    [song()?.album, song()?.artist, song()?.title]
      .filter(Boolean)
      .join(" - ") || "???";
  const lyrics = () => player.metadata?.current_song_lyrics;
  const isVtt = () =>
    (lyrics()?.length ?? 0) > 0 && lyrics()?.[0]?.id !== "raw";

  const scroll = (index: number) => {
    const lyricsDiv = lyricsRef();
    if (!lyricsDiv) {
      requestAnimationFrame(() => {
        scroll(index);
      });
      return;
    }

    let offsetPixels = 0;

    for (const [elementIndex, element] of lyricsDiv
      .querySelectorAll(`div.${styles.verse}`)
      .entries()) {
      if (index === elementIndex) {
        break;
      }

      offsetPixels -= element.clientHeight;
    }

    lyricsDiv.style.transform = `translate3d(0, ${offsetPixels.toString()}px, 0)`;
  };

  return (
    <Modal
      cropContent={isVtt()}
      id="song-lyrics"
      title="Acompanhar Letra"
      class={styles.body}
    >
      <div class={styles.outer}>
        <p>{currentSong()}</p>
        <div classList={{ [styles.inner]: true, [styles.vtt]: isVtt() }}>
          <Show when={isVtt()}>
            <div class={styles.marker}>
              <IconTablerChevronRight />
            </div>
          </Show>
          <Show
            when={lyrics()?.length}
            fallback={
              <div class={styles.warning}>
                Ainda não temos a letra desta música...
              </div>
            }
          >
            <div ref={setLyricsRef} class={styles.lyrics}>
              <Lyrics onShift={scroll} />
            </div>
          </Show>
        </div>
      </div>
    </Modal>
  );
}

interface LyricsProps {
  readonly onShift: (index: number) => void;
}

function Lyrics(props: LyricsProps) {
  const player = usePlayer();
  const fullLyrics = () => player.metadata?.current_song_lyrics;
  const progress = () => player.progress;
  const isVtt = () => fullLyrics()?.[0].id !== "raw";
  let currentVerseIndex = 0;

  createEffect(() => {
    const lyrics = fullLyrics();
    if (!isVtt()) {
      return;
    }

    for (const [index, { startTime, endTime }] of lyrics?.entries() ?? []) {
      if (
        startTime <= progress() &&
        progress() < endTime &&
        currentVerseIndex !== index
      ) {
        currentVerseIndex = index;
        props.onShift(index);
        break;
      }
    }
  });

  return (
    <Show
      when={isVtt()}
      fallback={<div class={styles["no-vtt"]}>{fullLyrics()?.[0].text}</div>}
    >
      <For each={fullLyrics()}>
        {(verse) => (
          <div
            classList={{
              [styles.verse]: true,
              [styles.current]:
                verse.startTime <= progress() && progress() < verse.endTime,
            }}
          >
            <Show
              when={verse.text !== "---"}
              fallback={
                <>
                  <CircleProgress
                    size="1em"
                    progress={
                      (progress() - verse.startTime) /
                      (verse.endTime - verse.startTime)
                    }
                  />
                  &nbsp;
                </>
              }
            >
              <div classList={{ [styles.authors]: verse.id === "songauthors" }}>
                <Show when={verse.id === "songauthors"}>
                  <b>Autoria:</b>{" "}
                </Show>
                {verse.text}
              </div>
            </Show>
          </div>
        )}
      </For>
    </Show>
  );
}
